import { ROUTES, TOUR_IDS } from "utils/constants";

const sel = (name) => `[data-tour="${name}"]`;

export const TOUR_STEPS = {
    [TOUR_IDS.SIDEBAR]: [
        {
            element: sel("sidebar-toggle"),
            popover: {
                title: "Collapse the Sidebar",
                description:
                    "Use this button to collapse or expand the navigation sidebar and free up space for reading prompts.",
                side: "right",
                align: "start",
            },
        },
        {
            element: sel("sidebar-favourites"),
            popover: {
                title: "Your Library",
                description:
                    "Everything you save, read, or haven't opened yet lives here, in three tabs: Saved, Read and Not read yet.",
                side: "right",
                align: "start",
            },
        },
        {
            element: sel("sidebar-profile"),
            popover: {
                title: "Profile & Account",
                description:
                    "View your profile, request a reassessment after completing the initial one, or manage your account settings from here.",
                side: "right",
                align: "end",
            },
        },
    ],

    [TOUR_IDS.PROMPTS]: [
        {
            element: sel("sidebar-toggle"),
            popover: {
                title: "Collapse the Sidebar",
                description:
                    "Tap this button anytime to collapse or expand the sidebar and get more room for your prompts.",
                side: "right",
                align: "start",
            },
        },
        {
            element: sel("prompt-leader"),
            popover: {
                title: "Leader Context",
                description:
                    "Each prompt is framed through a leader's perspective. Tap any leader here to hear the same question from them — the answers reload from the first dimension.",
                side: "bottom",
                align: "start",
            },
        },
        {
            element: sel("prompt-card"),
            popover: {
                title: "Reflection Prompt",
                description:
                    "Prompts may have multiple insight cards — swipe or use the dots below to navigate between them.",
                side: "right",
                align: "start",
            },
        },
        {
            element: sel("similar-prompts"),
            popover: {
                title: "Browse More Prompts",
                description:
                    "Signature Prompts appear here. Tap any prompt to open it.",
                side: "top",
                align: "center",
            },
        },
        {
            element: sel("help-menu"),
            popover: {
                title: "Need Help?",
                description:
                    "Tap the help icon anytime to restart this tour, send feedback, report an issue, or review our terms and privacy policy.",
                side: "top",
                align: "end",
            },
        },
    ],

};

/** Driver.js options shared by TourProvider — no page scroll during tours */
export const TOUR_DRIVER_CONFIG = {
    smoothScroll: false,
    allowScroll: false,
};

const STORAGE_KEY = "theecastable_tours_completed";

/** Maps pathname patterns to the tour shown on that page. */
export const ROUTE_TOUR_MAP = [
    { match: (path) => path === ROUTES.APP_ASSESSMENT, tourId: TOUR_IDS.ASSESSMENT },
    // { match: (path) => path === ROUTES.APP_PERSONALITY_DASHBOARD, tourId: TOUR_IDS.PERSONALITY_DASHBOARD },
    {
        match: (path) => path === ROUTES.APP_LIBRARY,
        tourId: TOUR_IDS.PROMPT_LIST,
    },
    {
        match: (path) => path === ROUTES.APP_PROMPTS || path.startsWith("/app/prompts"),
        tourId: TOUR_IDS.PROMPTS,
    },
];

export const getTourIdForPath = (pathname) => {
    const entry = ROUTE_TOUR_MAP.find(({ match }) => match(pathname));
    return entry?.tourId ?? null;
};

export const getStepsForTour = (tourId) => TOUR_STEPS[tourId] ?? [];

const readMap = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

const writeMap = (map) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    } catch {
        /* ignore quota / private mode */
    }
};

const userKey = (userId, tourId) => `${String(userId)}::${tourId}`;

export const isTourCompleted = (userId, tourId) => {
    if (!userId || !tourId) return false;
    const map = readMap();
    return map[userKey(userId, tourId)] === true;
};

export const setTourCompleted = (userId, tourId) => {
    if (!userId || !tourId) return;
    const map = readMap();
    map[userKey(userId, tourId)] = true;
    writeMap(map);
};

export const clearTourCompleted = (userId, tourId) => {
    if (!userId || !tourId) return;
    const map = readMap();
    delete map[userKey(userId, tourId)];
    writeMap(map);
};

export const clearAllToursForUser = (userId) => {
    if (!userId) return;
    const map = readMap();
    const prefix = `${String(userId)}::`;
    Object.keys(map).forEach((key) => {
        if (key.startsWith(prefix)) delete map[key];
    });
    writeMap(map);
};
